import { useCallback, useContext, useEffect, useState } from 'react';
import { toast } from '../../../util/toasts';
import { Typography } from '../../../components/typography/typography';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { debounce, isEqual } from 'lodash';
import './product-images.scss';
import Icon from '../../../components/icons/icon';
import { ProductImage } from '../../../clients/quipli-service/product/product';
import { FileField } from '../../../components/field/field';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { Button } from '../../../components/form/button';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { TenantContext } from '../../../context/tenant-context';
import classNames from 'classnames';

export const ProductImages = ({ productId }: { productId: number }) => {
  const [images, setImages] = useState<ProductImage[]>([]);
  const [imagesState, setImagesState] = useState<ProductImage[]>([]);
  const [uploads, setUploads] = useState<File[]>([]);
  const [altTitles, setAltTitles] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [originalAltTitles, setOriginalAltTitles] = useState<Record<string, string>>({}); // for comparison

  const { uploadToS3, getUploadProductImageUrl, addProductImage, updateProductImages } = useAPI();
  const { getProductImages } = useCustomerAPI();

  const refresh = useCallback(
    debounce(async () => {
      if (productId) {
        const pi = await getProductImages(productId, 'THUMB');
        setImages(pi);
        setImagesState(pi);
        const alts: Record<string, string> = {};
        for (const i of pi) {
          alts[i.id] = i.altTitle ?? '';
        }
        setAltTitles(alts);
        setOriginalAltTitles(alts);
      }
    }, 600),
    [productId],
  );

  useEffect(() => {
    refresh();

    return () => {
      setImages([]);
      setImagesState([]);
      setUploads([]);
      setAltTitles({});
      setOriginalAltTitles({});
    };
  }, []);

  useEffect(() => {
    if (uploads.length > 0) {
      const newPreviews = uploads.map((f) => URL.createObjectURL(f));
      setImagePreviews(newPreviews);
    } else {
      setImagePreviews([]);
    }
  }, [uploads]);

  const resetImages = () => {
    setImagesState(images);
    setAltTitles(originalAltTitles);
  };

  const uploadImages = async () => {
    if (uploads.length === 0) {
      return;
    }
    setIsUploading(true);
    try {
      await Promise.all(
        uploads.map(async (f, idx) => {
          try {
            const { url, imageId } = await getUploadProductImageUrl(productId, f.name);
            await uploadToS3(url, f);
            await addProductImage(productId, {
              imageId,
              filename: f.name,
              rank: idx,
            });
          } catch (e) {
            toast(`Error uploading ${f.name}`, 'error');
            console.error(e);
          }
        }),
      );
      toast(`Product images uploaded`);
    } catch (e) {
      toast(`Error uploading product images`, 'error');
      console.error(e);
    } finally {
      await refresh();
      setIsUploading(false);
      setUploads([]);
    }
  };

  const saveProductImages = async (newImages: ProductImage[]) => {
    try {
      await updateProductImages(
        productId,
        newImages.map((image, idx) => {
          return { id: image.id, rank: idx, altTitle: altTitles[image.id] };
        }),
      );
      setImages(newImages);
      setOriginalAltTitles(altTitles);
      toast(`Product images updated`);
    } catch (e) {
      toast(`Error updating product image order`, 'error');
      console.error(e);
    }
  };

  const reorderImages = (e: DragEndEvent) => {
    if (!e.over) return;
    const { active, over } = e;
    if (active.id !== over.id) {
      const oldIndex = imagesState.findIndex((image) => image.id === active.id);
      const newIndex = imagesState.findIndex((image) => image.id === over.id);
      const newImages = arrayMove(imagesState, oldIndex, newIndex);
      setImagesState(newImages);
      document.getElementById(`dragging-alt-${active.id}`)?.classList.toggle('hidden', true);
      document.getElementById(`not-dragging-alt-${active.id}`)?.classList.toggle('hidden', false);
    }
  };

  return (
    <div className="quipli-v2 product-images w-full text-center md:text-left">
      <div className="current-product-images">
        <DndContext onDragEnd={reorderImages}>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 sm:px-5">
            <SortableContext items={imagesState}>
              {imagesState.map((image) => {
                return image.id ? (
                  <DraggablePhoto id={image.id} className=" pt-4 rounded-lg overflow-hidden" key={image.filename}>
                    <div className="overflow-x-hidden px-2 hover:cursor-move">
                      <Image imageId={image.id} productId={image.productId} />
                    </div>
                    <div className={classNames({ hidden: true }, 'dragging-alt')}>
                      <div className=" flex gap-2 items-center justify-center">
                        <div className="py-3 px-1 break-all">
                          <Typography type="text-sm">
                            {image.filename.length > 19 ? `${image.filename.slice(0, 17)}...` : image.filename}
                          </Typography>
                        </div>
                        <Icon icon="Trash" size="medium" className="cursor-pointer" />
                      </div>
                      <div className={`flex gap-2 items-center justify-center`}>
                        <input
                          type="text"
                          placeholder="Alt Title"
                          value={altTitles[image.id] ?? ''}
                          className="w-full p-1 border border-solid border-gray-400 rounded-md"
                          readOnly={true}
                        />
                      </div>
                    </div>
                  </DraggablePhoto>
                ) : (
                  <></>
                );
              })}
            </SortableContext>
          </div>
        </DndContext>

        <div
          id={`not-dragging-alt`}
          className={classNames({ hidden: false }, 'grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 sm:px-5')}
        >
          {imagesState.map((image) => {
            return (
              <div>
                <div className="flex gap-2 items-center justify-center">
                  <div className="py-3 px-1 break-all">
                    <Typography type="text-sm">
                      {image.filename.length > 19 ? `${image.filename.slice(0, 17)}...` : image.filename}
                    </Typography>
                  </div>
                  <Icon
                    icon="Trash"
                    size="medium"
                    className="cursor-pointer"
                    onClick={() => {
                      setImagesState(imagesState.filter((i) => i.id !== image.id));
                    }}
                  />
                </div>
                <div className={`flex gap-2 items-center justify-center`}>
                  <input
                    type="text"
                    placeholder="Alt Title"
                    value={altTitles[image.id] ?? ''}
                    onChange={(e) => {
                      setAltTitles({ ...altTitles, [image.id]: e.target.value });
                    }}
                    className="w-full p-1 border border-solid border-gray-400 rounded-md"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {imagesState && (!isEqual(images, imagesState) || !isEqual(altTitles, originalAltTitles)) && (
        <div className="flex gap-2">
          <Button onClick={resetImages} className="button-secondary">
            Discard Changes
          </Button>
          <Button
            onClick={() => {
              saveProductImages(imagesState);
            }}
            className="button-primary"
          >
            Save
          </Button>
        </div>
      )}
      <div className=" my-2">
        <FileField
          onChange={(f) =>
            setUploads([
              ...uploads,
              ...(f instanceof Array
                ? f.map((v) => {
                    return v;
                  })
                : f
                ? [f]
                : []),
            ])
          }
          multiple={true}
          accept="image/jpeg, image/png"
          value={uploads.length === 0 ? undefined : uploads.map((f) => f)}
          customPreview={true}
        />
      </div>
      <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-2">
        {uploads.map((f, idx) => {
          return (
            <div className="grid grid-rows-1">
              <div className="flex items-center justify-between p-2 my-1 border border-solid border-gray-400 rounded-md gap-x-[4px]">
                {imagePreviews && imagePreviews[idx] && (
                  <img className="rounded-md duration-200 hover:scale-105 max-h-[50px]" src={imagePreviews[idx]} />
                )}
                <span className="width-[100px] inline break-all text-right">
                  <Typography type="text-md" className="text-grey-400 flex max-w-fit">
                    {f.name}
                  </Typography>
                </span>

                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setUploads([...uploads.slice(0, idx), ...(idx === uploads.length ? [] : uploads.slice(idx + 1))]);
                  }}
                >
                  <Icon icon="Close" size="medium" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {uploads.length > 0 && (
        <div className="flex flex-start my-2">
          <button className="button-primary" disabled={isUploading} onClick={() => uploadImages()}>
            Upload Image(s)
          </button>
        </div>
      )}
    </div>
  );
};

const DraggablePhoto = (props: any) => {
  const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
    id: props.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };
  useEffect(() => {
    const draggingItems = document.getElementsByClassName(`dragging-alt`);
    // const notDraggingItems = document.getElementsByClassName(`not-dragging-alt`);
    for (let i = 0; i < draggingItems.length; i++) {
      draggingItems[i].classList.toggle('hidden', !isDragging);
    }
    document.getElementById(`not-dragging-alt`)?.classList.toggle('hidden', isDragging);
  }, [isDragging]);
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} style={style}>
      {props.children}
    </div>
  );
};

export const Image = ({ imageId, productId, alt }: { imageId: string; productId: number; alt?: string }) => {
  const { tenant } = useContext(TenantContext);
  const bucketEnv =
    window.location.host === 'dev.quipli.com' || window.location.host.includes('localhost')
      ? 'dev'
      : window.location.host === 'qa.quipli.com'
      ? 'qa'
      : window.location.host === 'integration.quipli.com'
      ? 'integration'
      : window.location.host === 'beta.quipli.com'
      ? 'beta'
      : tenant === 'gameready'
      ? 'hipaa'
      : 'prod';
  const [imageSrc, setImageSrc] = useState<string>(
    `https://quipli-service-${bucketEnv}-public.s3.amazonaws.com/${tenant}/products/${productId}/images/${imageId}/THUMB`,
  );

  return (
    <img
      loading="lazy"
      className="h-[100px] object-cover shadow-lg shadow-gray-600  rounded-md duration-200 hover:scale-105"
      src={imageSrc}
      alt={alt}
      onError={() =>
        setImageSrc(`https://quipli-service-${bucketEnv}-public.s3.amazonaws.com/${tenant}/products/placeholder/THUMB`)
      }
    />
  );
};
